/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useState } from "react";
import { db } from "@/firebase";
import { collection, doc, getCountFromServer, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { reduceQuantity, increaseQuantity, addItemsToOrder, removeItemFromOrder, createEmptyOrder, updateOrder } from "../features/order/slice";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { useArchiveOrderMutation } from "@/features/organizations/api/ordersApi";
import { LocalStorage } from "@/constants/localStorage";
import { Item, Order, OrderItem, OrderStatus } from "@/types";
import useSnackbar from "@/hooks/useSnackbar";
import useTerminology from "./useTerminology";

const useOrder = () => {
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();
  const { t } = useTerminology();
  const order = useAppSelector((state: RootState) => state.order);
  const { organization } = useContext(WebsiteContext);
  const organizationId = useAppSelector((state) => state.ui.activeOrganization);
  const [archiveOrder] = useArchiveOrderMutation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);

  const orgId = organization ? organization.id : organizationId;

  const activeBranch = useAppSelector((state) => state.ui.activeBranch);

  const isIncluded = useCallback((id: string) => {
    return !!order.items.find((item: Item) => item.id === id);
  }, []);

  const increaseItemQuantity = useCallback((item: OrderItem) => {
    dispatch(increaseQuantity(item.id as unknown as OrderItem));
  }, []);

  const reduceItemQuantity = useCallback((id: string) => {
    dispatch(reduceQuantity(id));
  }, []);

  const checkOrderInLocalStorage = useCallback((organizationId: string) => {
    if (!organizationId) return;

    const lsOrganizationOrders = localStorage.getItem(LocalStorage.ORGANIZATION_ORDERS) ?? "{}";
    const organizationOrders = JSON.parse(lsOrganizationOrders);
    return organizationOrders[organizationId];
  }, []);

  const deleteLSOrganizationOrder = useCallback(() => {

    if (!orgId) return;

    const lsOrganizationOrders = localStorage.getItem(LocalStorage.ORGANIZATION_ORDERS) ?? "{}";
    const organizationOrders = JSON.parse(lsOrganizationOrders);
    delete organizationOrders[orgId];
    localStorage.setItem(LocalStorage.ORGANIZATION_ORDERS, JSON.stringify(organizationOrders));
  }, []);

  const subscribeToOrderByOrganizationId = useCallback(
    async (organizationId: string) => {
      const orderId = checkOrderInLocalStorage(organizationId);
      const docRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);

      const snap = await getDoc(docRef);

      return onSnapshot(
        docRef,
        (doc) => {
          if (doc.exists()) {
            const order = { ...doc.data(), id: doc.id } as Order;

            dispatch(updateOrder(order as Order));

            if ([OrderStatus.DELIVERED, OrderStatus.CANCELLED, OrderStatus.REJECTED].includes(order.status)) {
              deleteLSOrganizationOrder();
            }
          }
          else {
            if (!snap.exists()) {
              deleteLSOrganizationOrder();
            }
          }
        },
        (error) => {
          console.error(error);
        }
      );
    },
    [dispatch]
  );

  const subscribeToOrdersByOrganizationId = useCallback(
    (organizationId: string) => {
      const colRef = collection(db, `organizations/${organizationId}/orders`);

      return onSnapshot(
        colRef,
        (snapshot) => {
          const orders: Order[] = [];

          snapshot.forEach((doc) => {
            orders.push({ ...doc.data(), id: doc.id } as Order);
          });

          setOrders(orders);
        },
        (error) => {
          console.error(error);
        }
      );
    },
    [dispatch]
  );

  const fetchTotalOrdersCount = useCallback(async (organizationId: string, status?: string) => {
    let q = query(collection(db, `organizations/${organizationId}/orders`));
    if (status) {
      q = query(q, where("status", "==", status));
    }
    try {
      const snapshot = await getCountFromServer(q);
      setTotalOrdersCount(snapshot.data().count);
    } catch (error) {
      console.error("Error fetching total order count:", error);
      setTotalOrdersCount(0); // Reset or handle error
    }
  }, []);

  const getOrderInLocalStorage = useCallback((organizationId: string) => {
    if (!organizationId) return;
    return checkOrderInLocalStorage(organizationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveOrderInLocalStorage = useCallback((organizationId?: string, orderId?: string) => {
    if (!organizationId || !orderId) return;

    const lsOrganizationOrders = localStorage.getItem(LocalStorage.ORGANIZATION_ORDERS) ?? "{}";
    const organizationOrders = JSON.parse(lsOrganizationOrders);

    organizationOrders[organizationId] = orderId;

    localStorage.setItem(LocalStorage.ORGANIZATION_ORDERS, JSON.stringify(organizationOrders));
  }, []);

  const addItems = useCallback(async (items: OrderItem[]) => {
    if (!items || !orgId) return;

    const isOrder = "createdAt" in order;

    let orderId = order?.id;

    //Crear orden si no existe
    if (!isOrder) {
      const result = await dispatch(
        createEmptyOrder({
          organizationId: orgId,
          id: order?.id,
        })
      );

      if (createEmptyOrder.rejected.match(result) || !result.payload?.id) {
        openSnackbar("Error creando la orden", "error");
        return;
      }

      orderId = result.payload.id;
      saveOrderInLocalStorage(orgId, orderId);
    } else {
      saveOrderInLocalStorage(orgId, order?.id);
    }

    //Agregar items
    const result = await dispatch(
      addItemsToOrder({
        items,
        organizationId: orgId,
      })
    );

    if (addItemsToOrder.rejected.match(result)) {
      openSnackbar("Error al añadir el " + t("product"), "error");
      return;
    }

    openSnackbar(t("product", 1, true) + " añadido a la cuenta", "success");
  }, [order, orgId, activeBranch, organization]);

  const removeItem = useCallback((item: OrderItem) => {
    if (!item) return;
    dispatch(removeItemFromOrder({ organizationId: orgId, itemId: item?.id as string }));
  }, []);

  const archiveOldOrders = useCallback(() => {
    if (!orgId) return;

    const oneDayAfter = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    const orderCreatedAt = new Date(order?.createdAt);

    if (orderCreatedAt > oneDayAfter) {
      archiveOrder({
        order: order,
        organizationId: orgId,
        orderId: order?.id,
      });
      deleteLSOrganizationOrder();
    }
  }, [archiveOrder, order?.createdAt, order?.id, orgId]);

  return {
    order,
    orders,
    isIncluded,
    reduceItemQuantity,
    increaseItemQuantity,
    addItems,
    removeItem,
    getOrderInLocalStorage,
    subscribeToOrderByOrganizationId,
    subscribeToOrdersByOrganizationId,
    deleteLSOrganizationOrder,
    archiveOldOrders,
    checkOrderInLocalStorage,
    totalOrdersCount,
    fetchTotalOrdersCount,
  };
};

export default useOrder;
